import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { X, Sparkle, Crown, Loader2 } from "lucide-react";

/**
 * The Reciprocity Gateway — "Quick Registration". Triggered when a NOT-logged-in recipient
 * tries to send a Maison Reply. Creates their account (email/password OR Google), then hands
 * control back to the caller (ExperienceHub) to submit the held reply text and redirect to
 * the Maison Wallet — zero extra friction, their first purchase is now one click away.
 */
const QuickRegisterModal = ({ open, onClose, onRegistered }) => {
  const { register, loginWithGoogle } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [googleConfig, setGoogleConfig] = useState({ enabled: false, client_id: "" });
  const googleBtnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/auth/google/config").then((r) => setGoogleConfig(r.data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open || !googleConfig.enabled || !googleConfig.client_id) return;
    const handleCredential = async (response) => {
      setError(""); setLoading(true);
      try {
        const u = await loginWithGoogle(response.credential);
        onRegistered?.(u);
      } catch (err) {
        setError(err?.response?.data?.detail || "Google Sign-In failed.");
      } finally { setLoading(false); }
    };
    const initAndRender = () => {
      if (!window.google?.accounts?.id || !googleBtnRef.current) return;
      window.google.accounts.id.initialize({ client_id: googleConfig.client_id, callback: handleCredential });
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: "filled_black", size: "large", shape: "pill", width: 320, text: "continue_with",
      });
    };
    if (window.google?.accounts?.id) { initAndRender(); return; }
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.onload = initAndRender;
    document.body.appendChild(script);
    return () => { script.onload = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, googleConfig]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await register(name, email, password);
      onRegistered?.(u);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not create your account.");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-[hsl(38_30%_12%_/_0.6)] backdrop-blur-sm" data-testid="quick-register-modal" onClick={onClose}>
      <div className="surface rounded-[2rem] w-full max-w-sm p-7 relative animate-fade-up" onClick={(e) => e.stopPropagation()}>
        <button data-testid="quick-register-close" onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-full text-muted-foreground hover:bg-muted/50"><X className="w-4 h-4" /></button>
        <div className="flex items-center gap-2 mb-2 justify-center">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center"><Sparkle className="w-5 h-5 text-white" /></div>
        </div>
        <h3 className="font-serif text-2xl text-center mb-1">Join the Maison</h3>
        <p className="text-sm text-muted-foreground text-center mb-5">Create your free account to send this reply — and see your gift balance in the Maison Wallet.</p>

        {/* Fast-Lane Reciprocity — Google Sign-In prominently alongside the email form */}
        <div className="mb-4">
          {googleConfig.enabled ? (
            <div className="flex justify-center" ref={googleBtnRef} data-testid="quick-register-google-btn" />
          ) : (
            <button type="button" disabled
              className="w-full flex items-center justify-center gap-2 border border-border rounded-full py-2.5 text-sm text-muted-foreground opacity-60"
              data-testid="quick-register-google-placeholder">
              <Crown className="w-4 h-4 gold-text" /> Sign in with Google (not connected yet)
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 mb-4">
          <div className="flex-1 h-px bg-border" /><span className="text-xs text-muted-foreground">or</span><div className="flex-1 h-px bg-border" />
        </div>

        <form onSubmit={submit} className="space-y-3">
          <input data-testid="quick-register-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
            className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40" />
          <input data-testid="quick-register-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
            className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40" />
          <input data-testid="quick-register-password" required type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
            className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40" />
          {error && <div data-testid="quick-register-error" className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{error}</div>}
          <button data-testid="quick-register-submit" type="submit" disabled={loading}
            className="w-full bg-gradient-to-r from-primary to-secondary text-white rounded-xl py-2.5 hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account & send reply"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default QuickRegisterModal;
